/**
 * Sources: MTU `LEAP LLP Ultimate Life_20260813 R01.xlsx`, revision R01
 * (SHA-256: 15d2dee4937e5a2b421e9e7756e8ec6b6ed3c3f13a1035cd986c3dc53d3b9887),
 * and MTU `LEAP-1B_GenericInput_V14.xlsx`
 * (SHA-256: 9e8078848014653b3ba9c3de910376405a78c3f07fe675d3702a4dfab49cced3).
 * R01 supplies the current commercial limits and low/high/1B28B3 mapping;
 * Generic V14 supplies IINs, BBJ current limits, and current-only P/N
 * 364-600-010-0.
 *
 * Ultimate-Life limits (DR-3682) come from R01's ULTIMATE columns:
 * `Ultimate Life Limit Low Thrust` → LOW_VARIANTS, `High Thrust` →
 * HIGH_VARIANTS, `1B28B3` → B3_VARIANTS — the same variant lists the current
 * limits use. They are transcribed below in that column order and expanded into
 * the grouped `partUltimateLimits` shape (limitKey → variant keys) by the same
 * `limitsByValue` helper. BBJ1_VARIANTS and BBJ2_VARIANTS have no Ultimate
 * column, so no Ultimate group names them and a BBJ lookup resolves to nothing.
 * The value is the reachable ceiling, so every R01 P/N carries one — including
 * those whose Ultimate equals the current limit; current-only 364-600-010-0 has
 * none. `finalLimitPartNumbers` stays unset until MTU marks the P/Ns whose
 * ceiling CFMI fixed below Ultimate.
 *
 * R01's LPC spool typo 362-509-003-0 is corrected to 364-509-003-0 using
 * Generic V14 and CFM Rev 28. Generic V14 `_Note` rows require serial/SB
 * context that the exact-P/N catalog cannot express, so they are not copied as
 * duplicate keys; the active R01 current value is used for each base P/N.
 */
const LOW_VARIANTS = ['1B21', '1B23', '1B25', '1B27'];
const HIGH_VARIANTS = ['1B28', '1B28B1', '1B28B2', '1B28B2C'];
const B3_VARIANTS = ['1B28B3'];
const BBJ1_VARIANTS = ['1B28BBJ1'];
const BBJ2_VARIANTS = ['1B28BBJ2'];
const NA = 'N/A';
/** P/N -> Ultimate-Life limits, transcribed in the sheet's LOW / HIGH / 1B28B3 column order. */
const ULTIMATE_LIMITS = {
    '364-040-010-0': [30000, 30000, 30000],
    '364-040-011-0': [30000, 30000, 30000],
    '364-045-010-0': [30000, 30000, 30000],
    '364-045-020-0': [30000, 30000, 30000],
    '364-509-003-0': [30000, 30000, 30000],
    '364-509-004-0': [5000, 5000, 5000],
    '364-905-100-0': [30000, 30000, 30000],
    '2634M01G01': [15000, 10000, 10000],
    '2634M01G02': [20000, 17500, 15000],
    '2639M71G02': [20000, 17500, 15000],
    '2552M02P01': [15000, 10000, 10000],
    '2552M02P02': [20000, 17500, 15000],
    '2552M03G01': [15000, 10000, 10000],
    '2552M03G02': [20000, 17500, 15000],
    '2552M03G03': [20000, 17500, 15000],
    '2552M05P01': [20000, 17500, 15000],
    '2552M05P02': [20000, 17500, 15000],
    '2552M06G01': [10000, 9900, 9300],
    '2552M06G02': [10000, 10000, 10000],
    '2552M06G03': [20000, 17500, 15000],
    '2552M06G04': [20000, 17500, 15000],
    '2552M06G05': [20000, 17500, 15000],
    '2552M07P01': [20000, 17500, 15000],
    '2552M09P01': [20000, 17500, 15000],
    '2552M09P02': [20000, 17500, 15000],
    '2548M30G01': [20000, 20000, 20000],
    '2548M30G03': [13000, 13000, 13000],
    '2548M30G04': [13000, 13000, 13000],
    '2548M30G05': [13000, 13000, 13000],
    '2548M30G06': [20000, 20000, 20000],
    '2548M30G07': [30000, 30000, 30000],
    '2548M30G08': [30000, 30000, 30000],
    '2541M81G01': [30000, 30000, 30000],
    '2596M44P01': [14500, 10000, 10000],
    '2596M44P02': [20000, 17500, 15000],
    '2600M21G02': [20000, 17500, 15000],
    '2600M21G04': [20000, 17500, 15000],
    '2600M25P01': [20000, 17500, 15000],
    '2600M25P02': [20000, 17500, 15000],
    '2600M25P03': [20000, 17500, 15000],
    '2547M01G01': [15200, 14900, 12300],
    '2547M01G02': [20000, 17500, 15000],
    '2547M01G03': [20000, 17500, 15000],
    '2547M40P01': [13300, 10000, 10000],
    '2547M40P02': [20000, 17500, 15000],
    '364-021-010-0': [2500, 2500, 2500],
    '364-021-020-0': [30000, 30000, 25000],
    '364-021-021-0': [30000, 30000, 25000],
    '364-021-030-0': [30000, 30000, 25000],
    '364-021-031-0': [30000, 30000, 25000],
    '364-021-032-0': [30000, 30000, 25000],
    '364-021-110-0': [2700, 2700, 2700],
    '364-021-120-0': [30000, 30000, 25000],
    '364-021-130-0': [30000, 30000, 25000],
    '364-021-131-0': [30000, 30000, 25000],
    '364-021-210-0': [4000, 4000, 4000],
    '364-021-220-0': [30000, 30000, 25000],
    '364-021-230-0': [30000, 30000, 25000],
    '364-021-231-0': [30000, 30000, 25000],
    '364-001-611-0': [30000, 30000, 25000],
    '364-001-612-0': [30000, 30000, 25000],
    '364-600-011-0': [30000, 30000, 25000],
    '364-600-013-0': [30000, 30000, 25000],
    '364-600-014-0': [30000, 30000, 25000],
    '364-035-610-0': [30000, 30000, 25000],
};
function limitsByValue(buckets) {
    const grouped = new Map();
    for (const [limitValue, variants] of buckets) {
        if (limitValue === undefined)
            continue;
        const key = String(limitValue);
        grouped.set(key, [...(grouped.get(key) ?? []), ...variants]);
    }
    return Object.fromEntries(grouped);
}
function ultimateLimitsFor(parts) {
    const partNumbers = new Set(parts.map(([partNumber]) => partNumber));
    return Object.fromEntries(Object.entries(ULTIMATE_LIMITS)
        .filter(([partNumber]) => partNumbers.has(partNumber))
        .map(([partNumber, [low, high, b3]]) => [
        partNumber,
        limitsByValue([
            [low, LOW_VARIANTS],
            [high, HIGH_VARIANTS],
            [b3, B3_VARIANTS],
        ]),
    ]));
}
function component(componentName, iin, parts) {
    return {
        componentName,
        iin,
        partNumbers: parts.map(([partNumber]) => partNumber),
        partLimits: Object.fromEntries(parts.map(([partNumber, [low, high, b3, bbj1, bbj2]]) => [
            partNumber,
            limitsByValue([
                [low, LOW_VARIANTS],
                [high, HIGH_VARIANTS],
                [b3, B3_VARIANTS],
                [bbj1, BBJ1_VARIANTS],
                [bbj2, BBJ2_VARIANTS],
            ]),
        ])),
        partUltimateLimits: ultimateLimitsFor(parts),
    };
}
export const leap_1b = {
    engineType: 'LEAP-1B',
    groups: [
        {
            groupName: 'Fan Module',
            components: [
                component('Fan Disk', '218', [
                    ['364-040-010-0', [21600, 21600, 21600, 10000, 10000]],
                    ['364-040-011-0', [21600, 21600, 21600, 10000, 10000]],
                ]),
                component('LP Compressor Shaft', '226', [
                    ['364-045-010-0', [30000, 30000, 30000, 10000, 10000]],
                    ['364-045-020-0', [30000, 30000, 30000, 10000, 10000]],
                ]),
                component('LP Compressor Spool', '219', [
                    ['364-509-003-0', [30000, 30000, 30000, NA, NA]],
                    ['364-509-004-0', [5000, 5000, 5000, NA, NA]],
                    ['364-905-100-0', [30000, 30000, 30000, 10000, 10000]],
                ]),
            ],
        },
        {
            groupName: 'HPC Module',
            components: [
                component('Compressor Rotor Stage 1 Blisk', '433', [
                    ['2634M01G01', [15000, 10000, 10000, 10000, 10000]],
                    ['2634M01G02', [20000, 17500, 15000, 10000, 10000]],
                    ['2639M71G02', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('Compressor Rotor Stage 2 Blisk', '434', [
                    ['2552M02P01', [15000, 10000, 10000, 10000, 10000]],
                    ['2552M02P02', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('Compressor Rotor Stage 3-4 Blisk', '435', [
                    ['2552M03G01', [15000, 10000, 10000, 10000, 10000]],
                    ['2552M03G02', [20000, 17500, 15000, 10000, 10000]],
                    ['2552M03G03', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('Compressor Rotor Stage 5 Blisk', '436', [
                    ['2552M05P01', [20000, 17500, 15000, 10000, 10000]],
                    ['2552M05P02', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('Compressor Rotor Stages 6-10 Spool', '437', [
                    ['2552M06G01', [10000, 9900, 9300, 5000, 5000]],
                    ['2552M06G02', [10000, 10000, 10000, 10000, 10000]],
                    ['2552M06G03', [20000, 17500, 15000, 10000, 10000]],
                    ['2552M06G04', [20000, 17500, 15000, 10000, 10000]],
                    ['2552M06G05', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('Impeller Tube Support', '431', [['2552M07P01', [20000, 17500, 15000, 10000, 10000]]]),
                component('Rotating Seal', '432', [
                    ['2552M09P01', [20000, 17500, 15000, 10000, 10000]],
                    ['2552M09P02', [20000, 17500, 15000, 10000, 10000]],
                ]),
            ],
        },
        {
            groupName: 'HPT Module',
            components: [
                component('Combustion Case Assembly', '463', [
                    ['2548M30G01', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G03', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G04', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G05', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G06', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G07', [13000, 13000, 13000, 13000, 13000]],
                    ['2548M30G08', [13000, 13000, 13000, 13000, 13000]],
                ]),
                component('HPT Case', '488', [['2541M81G01', [15000, 15000, 15000, 15000, 15000]]]),
                component('Forward Outer Seal', '492', [
                    ['2596M44P01', [14500, 10000, 10000, 3000, 3000]],
                    ['2596M44P02', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('HPT Rotor Stage 1 Disk', '495', [
                    ['2600M21G02', [11200, 10000, 8700, 5000, 5000]],
                    ['2600M21G04', [20000, 17500, 15000, undefined, undefined]],
                ]),
                component('HPT Rotor Mid Seal', '493', [
                    ['2600M25P01', [20000, 17500, 15000, 9000, 9000]],
                    ['2600M25P02', [20000, 17500, 15000, 10000, 10000]],
                    ['2600M25P03', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('HPT Rotor Stage 2 Disk', '496', [
                    ['2547M01G01', [11600, 11300, 9400, 10000, 10000]],
                    ['2547M01G02', [20000, 17500, 15000, 10000, 10000]],
                    ['2547M01G03', [20000, 17500, 15000, 10000, 10000]],
                ]),
                component('HPT Aft Rotating Seal', '494', [
                    ['2547M40P01', [13300, 10000, 10000, 5000, 5000]],
                    ['2547M40P02', [20000, 17500, 15000, 10000, 10000]],
                ]),
            ],
        },
        {
            groupName: 'LPT Module',
            components: [
                component('LP Turbine Stage 1 Disk', '603', [
                    ['364-021-010-0', [2500, 2500, 2500, NA, NA]],
                    ['364-021-020-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-021-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-030-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-031-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-032-0', [14100, 14100, 14100, 3500, 3500]],
                ]),
                component('LP Turbine Stage 2 Disk', '604', [
                    ['364-021-110-0', [2700, 2700, 2700, NA, NA]],
                    ['364-021-120-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-130-0', [14100, 14100, 14100, 3500, 3500]],
                    ['364-021-131-0', [14100, 14100, 14100, 3500, 3500]],
                ]),
                component('LP Turbine Stage 3 Disk', '605', [
                    ['364-021-210-0', [4000, 4000, 4000, NA, NA]],
                    ['364-021-220-0', [11300, 11300, 11300, 3500, 3500]],
                    ['364-021-230-0', [11300, 11300, 11300, 3500, 3500]],
                    ['364-021-231-0', [11300, 11300, 11300, 3500, 3500]],
                ]),
                component('LP Turbine Stage 4 Disk', '608', [
                    ['364-001-611-0', [17500, 17500, 17500, 3500, 3500]],
                    ['364-001-612-0', [17500, 17500, 17500, 3500, 3500]],
                ]),
                component('LP Turbine Stage 5 Disk', '607', [
                    ['364-600-011-0', [20000, 20000, 20000, 3500, 3500]],
                    ['364-600-013-0', [20000, 20000, 20000, 3500, 3500]],
                    ['364-600-014-0', [20000, 20000, 20000, 3500, 3500]],
                    ['364-600-010-0', [12000, 12000, 12000, NA, NA]],
                ]),
                component('LP Turbine Shaft', '619', [['364-035-610-0', [20000, 20000, 20000, 3500, 3500]]]),
            ],
        },
    ],
};
//# sourceMappingURL=leap-1b.js.map